// Extracted: products-only list (was ShopTab.jsx pre-services).
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage, HiddenBadge } from './_shared';
import ProductForm from './ProductForm';

export default function ProductsList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/admin/products'); // includes hidden products
      setProducts(r.data);
    } catch {
      toast.error('Failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createProduct = async (form) => {
    try {
      await axiosInstance.post('/admin/products', form);
      toast.success('Product created!');
      setView('list');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const updateProduct = async (form) => {
    try {
      await axiosInstance.put(`/admin/products/${editing.id}`, form);
      toast.success('Product updated!');
      setView('list');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed'));
    }
  };

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axiosInstance.delete(`/admin/products/${id}`);
      toast.success('Product deleted');
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to delete'));
    }
  };

  if (view === 'create')
    return (
      <div>
        <button
          onClick={() => setView('list')}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4"
        >
          <i className="ph-bold ph-arrow-left" /> Back
        </button>
        <h2 className="text-lg font-extrabold mb-4">Add Product</h2>
        <ProductForm onSave={createProduct} onCancel={() => setView('list')} />
      </div>
    );

  if (view === 'edit')
    return (
      <div>
        <button
          onClick={() => setView('list')}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 mb-4"
        >
          <i className="ph-bold ph-arrow-left" /> Back
        </button>
        <h2 className="text-lg font-extrabold mb-4">Edit Product</h2>
        <ProductForm initial={editing} onSave={updateProduct} onCancel={() => setView('list')} />
      </div>
    );

  return (
    <div className="space-y-4">
      <button
        onClick={() => setView('create')}
        className="w-full py-3 bg-[#2B3A67] text-white font-extrabold rounded-2xl flex items-center justify-center gap-2"
      >
        <i className="ph-bold ph-plus" /> Add Product
      </button>
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E54562]" />
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <div
              key={p.id}
              className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 ${
                p.is_active === false ? 'opacity-60' : ''
              }`}
            >
              {p.images?.[0] && (
                <img
                  src={p.images[0]}
                  alt=""
                  className="w-14 h-14 rounded-xl object-cover shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="font-extrabold truncate">{p.name}</p>
                  {p.is_active === false && <HiddenBadge />}
                </div>
                <p className="text-xs text-gray-400">
                  {p.brand} · {p.category}
                </p>
                <p className="text-sm font-bold text-[#E54562] mt-0.5">
                  ₹{p.discounted_price || p.base_price}
                  {p.discounted_price && (
                    <span className="text-xs text-gray-400 line-through ml-1">
                      ₹{p.base_price}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => {
                    setEditing(p);
                    setView('edit');
                  }}
                  className="px-3 py-2 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs rounded-xl"
                  data-testid={`edit-product-${p.id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteProduct(p.id, p.name)}
                  className="px-3 py-2 border-2 border-[#E54562] text-[#E54562] font-bold text-xs rounded-xl hover:bg-[#E54562] hover:text-white transition-colors"
                  data-testid={`delete-product-${p.id}`}
                >
                  <i className="ph-bold ph-trash" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
